/* No dependencies; safe to reinitialize when Shopify reloads a section. */
(() => {
  if (customElements.get('bb-editorial-home')) return;
  class BBEditorialHome extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      this._signal = this._abort.signal;
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._mobile = window.matchMedia('(max-width: 749px)');
      this._videos = new Map();
      this._frame = 0;
      this._header = document.querySelector('bb-site-header .bb-header');
      this._hero = this.querySelector('.bb-hero');
      this._hint = this.querySelector('.bb-scroll-hint');
      this._rail = this.querySelector('.bb-rail');
      this._observerAvailable = 'IntersectionObserver' in window;
      this._manualFilms = this._motion.matches || Boolean(navigator.connection?.saveData);
      const on = (target, type, fn, options = {}) => target?.addEventListener(type, fn, { ...options, signal: this._signal });
      on(this, 'click', event => this._click(event));
      on(window, 'scroll', () => this._queueUpdate(), { passive: true });
      on(window, 'resize', () => this._queueUpdate(), { passive: true });
      on(document, 'visibilitychange', () => this._syncVideos());
      on(this._motion, 'change', () => {
        this._manualFilms = this._motion.matches || Boolean(navigator.connection?.saveData);
        this._videos.forEach(state => { state.userPlaying = false; });
        this._syncVideos();
      });
      on(this._mobile, 'change', () => this._syncVideos());
      on(navigator.connection, 'change', () => {
        this._manualFilms = this._motion.matches || Boolean(navigator.connection?.saveData);
        this._syncVideos();
      });
      on(document, 'shopify:block:select', event => {
        const target = document.getElementById(`bb-block-${event.detail?.blockId}`);
        if (target && this.contains(target)) target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      });
      if (this._observerAvailable) {
        this._revealObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              if (this.dataset.replay !== 'true') this._revealObserver.unobserve(entry.target);
            } else if (entry.intersectionRatio === 0 && this.dataset.replay === 'true') {
              entry.target.classList.remove('is-revealed');
            }
          }
        }, { threshold: .12 });
        this.querySelectorAll('[data-bb-reveal]').forEach(element => this._revealObserver.observe(element));
        this.classList.add('bb-enhanced');
        this._videoObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            const state = this._videos.get(entry.target);
            if (state) state.visible = entry.isIntersecting;
          }
          this._syncVideos();
        }, { threshold: .1 });
      }
      for (const video of this.querySelectorAll('video.bb-video')) {
        const state = { visible: !this._observerAvailable, userPaused: false, userPlaying: false, failed: false, pending: false };
        this._videos.set(video, state);
        video.muted = true;
        video.playsInline = true;
        video.removeAttribute('autoplay');
        const media = video.closest('.bb-media');
        state.button = media?.querySelector(video.classList.contains('bb-video-mobile') ? '[data-bb-film="mobile"]' : '[data-bb-film="desktop"]');
        if (state.button) state.button.hidden = false;
        on(video, 'playing', () => {
          video.classList.add('is-ready');
          this._labelVideo(video);
          // A pending play promise can finish after the user scrolls away.
          if (!this._shouldPlay(video, state)) video.pause();
        });
        on(video, 'pause', () => this._labelVideo(video));
        on(video, 'error', () => {
          state.failed = true;
          video.classList.remove('is-ready');
          if (state.button) state.button.hidden = true;
        });
        this._videoObserver?.observe(video);
        this._labelVideo(video);
      }
      if (this._rail) {
        on(this._rail, 'scroll', () => this._updateRail(), { passive: true });
        on(this._rail, 'keydown', event => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target !== this._rail) return;
          event.preventDefault();
          this._moveRail(event.key === 'ArrowRight' ? 1 : -1);
        });
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(() => this._updateRail());
          this._resizeObserver.observe(this._rail);
        }
        this.querySelectorAll('[data-bb-rail-controls]').forEach(element => { element.hidden = false; });
        this._updateRail();
      }
      this.querySelectorAll('[data-bb-js]').forEach(element => { element.hidden = false; });
      this._update();
      this._syncVideos();
    }
    disconnectedCallback() {
      this._videos?.forEach((_,video) => video.pause());
      this._abort?.abort();
      this._abort = null;
      this._revealObserver?.disconnect();
      this._videoObserver?.disconnect();
      this._resizeObserver?.disconnect();
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;
    }
    _queueUpdate() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => { this._frame = 0; this._update(); });
    }
    _update() {
      const box = this._hero?.getBoundingClientRect();
      this._hint?.classList.toggle('is-dismissed', Boolean(box && box.top < -90));
      this._updateRail();
      if (!this._observerAvailable) {
        this._videos.forEach((state,video) => {
          const rect = video.getBoundingClientRect();
          state.visible = rect.bottom > 0 && rect.top < window.innerHeight;
        });
        this._syncVideos();
      }
    }
    _click(event) {
      const scroll = event.target.closest('[data-bb-scroll]');
      if (scroll) {
        let target;
        try { target = this.querySelector(scroll.dataset.bbScroll); } catch { return; }
        if (target) {
          event.preventDefault();
          const top = target.getBoundingClientRect().top + window.scrollY - (this._header?.offsetHeight || 0);
          window.scrollTo({ top, behavior: this._motion.matches ? 'auto' : 'smooth' });
        }
      }
      const rail = event.target.closest('[data-bb-rail]');
      if (rail) this._moveRail(Number(rail.dataset.bbRail));
      const film = event.target.closest('[data-bb-film]');
      if (film) {
        const video = film.closest('.bb-media')?.querySelector(film.dataset.bbFilm === 'mobile' ? '.bb-video-mobile' : '.bb-video-desktop');
        const state = this._videos.get(video);
        if (!state || state.failed) return;
        if (video.paused) { state.userPaused = false; state.userPlaying = true; }
        else { state.userPaused = true; state.userPlaying = false; }
        this._syncVideos();
      }
    }
    _shouldPlay(video,state) {
      return !state.failed && state.visible && !document.hidden && video.getClientRects().length > 0 && !state.userPaused && (!this._manualFilms || state.userPlaying);
    }
    _syncVideos() {
      this._videos.forEach((state, video) => {
        if (!this._shouldPlay(video,state)) { video.pause(); this._labelVideo(video); return; }
        if (!video.paused || state.pending) return;
        state.pending = true;
        const attempt = video.play();
        Promise.resolve(attempt).catch(() => {
          state.userPaused = true;
          state.userPlaying = false;
        }).finally(() => {
          state.pending = false;
          if (!this.isConnected || !this._shouldPlay(video,state)) video.pause();
          this._labelVideo(video);
        });
      });
    }
    _labelVideo(video) {
      const button = this._videos.get(video)?.button;
      if (!button) return;
      button.textContent = video.paused ? (this.dataset.playLabel || 'Play film') : (this.dataset.pauseLabel || 'Pause film');
      button.setAttribute('aria-label', button.textContent);
      button.setAttribute('aria-pressed', String(!video.paused));
    }
    _moveRail(direction) {
      if (!this._rail) return;
      const card = this._rail.querySelector('.bb-card');
      const step = (card?.getBoundingClientRect().width || this._rail.clientWidth * .8) + (parseFloat(getComputedStyle(this._rail).gap) || 0);
      this._rail.scrollBy({ left: direction * step, behavior: this._motion.matches ? 'auto' : 'smooth' });
    }
    _updateRail() {
      if (!this._rail) return;
      const atStart = this._rail.scrollLeft <= 2;
      const atEnd = this._rail.scrollLeft + this._rail.clientWidth >= this._rail.scrollWidth - 3;
      this.querySelectorAll('[data-bb-rail="-1"]').forEach(button => { button.disabled = atStart; });
      this.querySelectorAll('[data-bb-rail="1"]').forEach(button => { button.disabled = atEnd; });
    }
  }
  customElements.define('bb-editorial-home', BBEditorialHome);
})();
